import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { Drawer } from "@made-people/blueprint-drawer";
import { overlayEventEmitter } from "@made-people/blueprint-overlay";
import { useHref } from "@made-people/grease-link";
import {
  useProductBase,
  useProductPurchaseInfo,
  useRelatedProducts,
} from "@made-people/grease-product";
import { useGreaseGrowthbookFeatureValue } from "@made-people/grease-growthbook";
import { useGtm } from "@made-people/grease-gtm";
import { useTranslate } from "@made-people/grease-translations";
import { Cross } from "../../../assets/icons/Cross";
import { useGeneralSettings } from "../../../hooks/use-general-settings";
import { parseComfortSuiteSettings } from "../settings";
import { CustomisationFeatures } from "./customisation-features";
import { FeatureNotice } from "./feature-notice";
import { useCustomisationDrawerState } from "./use-customisation-drawer-state";
import { useCustomisationTracking } from "./use-customisation-tracking";

export const CustomisationDrawer = () => {
  const translate = useTranslate();
  const navigate = useNavigate();
  const href = useHref();
  const { pushCustomEvent } = useGtm();
  const productBase = useProductBase();
  const purchaseInfo = useProductPurchaseInfo();
  const relatedProducts = useRelatedProducts();
  const customisationModalEnabled = useGreaseGrowthbookFeatureValue(
    "comfort-suite:customisation-modal",
    false,
  );
  const generalSettings = useGeneralSettings();
  const { customisationFeatures } = useMemo(
    () => parseComfortSuiteSettings(generalSettings),
    [generalSettings],
  );

  const {
    isDetailedView,
    setIsDetailedView,
    activeNotice,
    setActiveNotice,
    currentUrl,
    setCurrentUrl,
    targetProduct,
    setTargetProduct,
    sourceUrl,
  } = useCustomisationDrawerState(productBase);
  const [hasChanged, setHasChanged] = useState(false);
  const [dismissedNotice, setDismissedNotice] = useState(false);

  const closeDrawer = useCallback(() => {
    overlayEventEmitter.emit("close", {
      overlayId: "comfort-suite-customisation",
    });
  }, []);

  const { handleSubmit } = useCustomisationTracking({
    href,
    navigate,
    pushCustomEvent,
    productBase,
    purchaseInfo,
    customisationFeatures,
    targetProduct,
    sourceUrl,
    currentUrl,
    onClose: closeDrawer,
  });

  if (!customisationModalEnabled) {
    return null;
  }

  return (
    <Drawer
      customOverlayId="comfort-suite-customisation"
      direction="right"
      hasHandle={false}
      header={{
        headingSettings: {
          heading: translate("Customisation_Popup_Heading"),
        },
        closeIcon: (
          <span role="img" aria-hidden="true">
            <Cross className="w-3 h-3" />
          </span>
        ),
      }}
    >
      <Drawer.Content
        contentSettings={{
          className: "w-full h-[100dvh] overflow-hidden",
        }}
      >
        <div className="mx-auto flex h-full w-full flex-col lg:min-w-[400px] lg:w-[25vw]">
          <div className="bg-[#F0ECE4] px-6 py-3 text-center text-body-sm">
            {translate("Customisation_Popup_Top_Bar_Return_Policy")}
          </div>

          <div className="flex flex-row items-center justify-end gap-4 pt-6 text-body-sm">
            <span className="text-black/60">
              {translate("Customisation_Popup_Views_View")}
            </span>
            <button
              className={isDetailedView ? "underline underline-offset-2" : ""}
              onClick={() => setIsDetailedView(true)}
            >
              {translate("Customisation_Popup_Views_Detailed")}
            </button>
            <button
              className={!isDetailedView ? "underline underline-offset-2" : ""}
              onClick={() => setIsDetailedView(false)}
            >
              {translate("Customisation_Popup_Views_Overview")}
            </button>
          </div>

          <section className="flex-1 overflow-y-auto pt-6 pb-20">
            <CustomisationFeatures
              view={isDetailedView}
              productBase={productBase}
              relatedProducts={relatedProducts}
              features={customisationFeatures}
              onUrlChange={setCurrentUrl}
              onTargetProductChange={(product) => {
                setHasChanged(true);
                setDismissedNotice(false);
                setTargetProduct(product);
              }}
              onNoticeChange={(active) => {
                if (!dismissedNotice) {
                  setActiveNotice(active);
                }
              }}
            />
          </section>

          <div className="sticky bottom-0 mt-auto">
            {hasChanged && activeNotice && (
              <div className="px-6 pb-4">
                <FeatureNotice
                  onClose={() => {
                    setDismissedNotice(true);
                    setActiveNotice(false);
                  }}
                />
              </div>
            )}
            <div className="bg-[#faf9f6] px-6 pt-4">
              <button
                className="w-full rounded-md bg-black px-6 py-4 text-white text-body-sm"
                onClick={handleSubmit}
              >
                {translate("Customisation_Popup_Submit_Button")}
              </button>
            </div>
          </div>
        </div>
      </Drawer.Content>
    </Drawer>
  );
};
